if (!customElements.get('quick-add-modal')) {
  customElements.define('quick-add-modal', class QuickAddModal extends ModalDialog {
    constructor() {
      super();
      this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');
    }

    hide(preventFocus = false) {
      const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      if (cartNotification) cartNotification.setActiveElement(this.openedBy);
      this.modalContent.innerHTML = '';

      if (preventFocus) this.openedBy = null;
      super.hide();
    }

    show(opener) {
      opener.setAttribute('aria-disabled', true);
      opener.classList.add('loading');
      opener.querySelector('.loading-overlay__spinner').classList.remove('hidden');

      fetch(opener.getAttribute('data-product-url'))
        .then((response) => response.text())
        .then((responseText) => {
          const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
          this.productElement = responseHTML.querySelector('section[id^="MainProduct-"]');
          this.preventDuplicatedIDs();
          this.removeDOMElements();
          this.setInnerHTML(this.modalContent, this.productElement.innerHTML);

          if (window.Shopify && Shopify.PaymentButton) {
            Shopify.PaymentButton.init();
          }

          if (window.ProductModel) window.ProductModel.loadShopifyXR();

          this.removeGalleryListSemantic();
          this.updateImageSizes();
          this.preventVariantURLSwitching();
          super.show(opener);
        })
        .finally(() => {
          opener.removeAttribute('aria-disabled');
          opener.classList.remove('loading');
          opener.querySelector('.loading-overlay__spinner').classList.add('hidden');
        });
    }

    setInnerHTML(element, html) {
      element.innerHTML = html;

      // Reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
      element.querySelectorAll('script').forEach(oldScriptTag => {
        const newScriptTag = document.createElement('script');
        Array.from(oldScriptTag.attributes).forEach(attribute => {
          newScriptTag.setAttribute(attribute.name, attribute.value)
        });
        newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
        oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
      });
    }

    preventVariantURLSwitching() {
      this.modalContent.querySelector('variant-radios,variant-selects').setAttribute('data-update-url', 'false');
    }

    removeDOMElements() {
      const pickupAvailability = this.productElement.querySelector('pickup-availability');
      if (pickupAvailability) pickupAvailability.remove();

      const productModal = this.productElement.querySelector('product-modal');
      if (productModal) productModal.remove();
    }

    preventDuplicatedIDs() {
      const sectionId = this.productElement.dataset.section;
      this.productElement.innerHTML = this.productElement.innerHTML.replaceAll(sectionId, `quickadd-${ sectionId }`);
      this.productElement.querySelectorAll('variant-selects, variant-radios').forEach((variantSelect) => {
        variantSelect.dataset.originalSection = sectionId;
      });
    }

    removeGalleryListSemantic() {
      const galleryList = this.modalContent.querySelector('[id^="Slider-Gallery"]');
      if (!galleryList) return;

      galleryList.setAttribute('role', 'presentation');
      galleryList.querySelectorAll('[id^="Slide-"]').forEach(li => li.setAttribute('role', 'presentation'));
    }

    updateImageSizes() {
      const product = this.modalContent.querySelector('.product');
      const desktopColumns = product.classList.contains('product--columns');
      if (!desktopColumns) return;

      const mediaImages = product.querySelectorAll('.product__media img');
      if (!mediaImages.length) return;

      let mediaImageSizes = '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';

      if (product.classList.contains('product--medium')) {
        mediaImageSizes = mediaImageSizes.replace('715px', '605px');
      } else if (product.classList.contains('product--small')) {
        mediaImageSizes = mediaImageSizes.replace('715px', '495px');
      }

      mediaImages.forEach(img => img.setAttribute('sizes', mediaImageSizes));
    }
  });
}

if (!customElements.get('inline-quick-add')) {
  customElements.define('inline-quick-add', class InlineQuickAdd extends HTMLElement {
    constructor() {
      super();
      this.sizeButtons = this.querySelectorAll('.inline-quick-add__size-btn');
      this.cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');

      this.sizeButtons.forEach(btn =>
        btn.addEventListener('click', this.addToCart.bind(this))
      );
    }

    async addToCart(event) {
      const btn = event.currentTarget;
      const variantId = btn.dataset.variantId;
      const originalText = btn.textContent;

      btn.disabled = true;
      btn.classList.add('is-loading');

      try {
        const formData = new FormData();
        formData.append('id', variantId);
        formData.append('quantity', 1);

        if (this.cart && typeof this.cart.getSectionsToRender === 'function') {
          const sections = this.cart.getSectionsToRender().map(s => s.id);
          formData.append('sections', sections.join(','));
          formData.append('sections_url', window.location.pathname);
        }

        const response = await fetch(`${window.routes.cart_add_url}`, {
          method: 'POST',
          body: formData,
          headers: {
            'Accept': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          }
        });

        const data = await response.json();

        if (!response.ok || data.status) {
          throw new Error(data.description || data.message || `Cart error (${response.status})`);
        }

        btn.classList.remove('is-loading');
        btn.classList.add('is-added');
        btn.textContent = '✓';

        // Fire analytics event so tracking pixels (Meta, Google, TikTok, Klaviyo, Triple Whale, Attentive, etc.) pick up the ATC
        try {
          if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
            const currency = (window.Shopify.currency && window.Shopify.currency.active) || 'USD';
            const items = data.items || [data];
            items.forEach(item => {
              window.Shopify.analytics.publish('product_added_to_cart', {
                cartLine: {
                  merchandise: {
                    id: 'gid://shopify/ProductVariant/' + (item.variant_id || item.id),
                    price: { amount: (item.price || 0) / 100, currencyCode: currency },
                    product: {
                      id: 'gid://shopify/Product/' + item.product_id,
                      title: item.product_title || item.title || '',
                      untranslatedTitle: item.product_title || item.title || '',
                      vendor: item.vendor || '',
                      type: item.product_type || ''
                    },
                    sku: item.sku || '',
                    title: item.variant_title || '',
                    untranslatedTitle: item.variant_title || ''
                  },
                  quantity: item.quantity || 1,
                  cost: {
                    totalAmount: {
                      amount: ((item.final_line_price || item.line_price || item.price) || 0) / 100,
                      currencyCode: currency
                    }
                  }
                }
              });
            });
          }
          document.dispatchEvent(new CustomEvent('cart:item-added', { detail: data }));
        } catch (analyticsErr) {
          console.warn('Analytics publish failed (non-fatal):', analyticsErr);
        }

        if (this.cart && typeof this.cart.renderContents === 'function') {
          try {
            // Dawn's renderContents misses the is-empty class on the outer
            // <cart-drawer> and <cart-drawer-items>. On first add from an
            // empty cart, items don't show until manually cleared.
            this.cart.classList.remove('is-empty');
            const cartDrawerItems = this.cart.querySelector('cart-drawer-items');
            if (cartDrawerItems) cartDrawerItems.classList.remove('is-empty');

            this.cart.renderContents(data);
          } catch (renderErr) {
            console.warn('Cart render failed, will reload:', renderErr);
            window.location.reload();
            return;
          }
        }

        setTimeout(() => {
          btn.classList.remove('is-added');
          btn.textContent = originalText;
          btn.disabled = false;
        }, 1500);

      } catch (err) {
        console.error('Add to cart failed:', err);
        btn.classList.remove('is-loading');
        btn.disabled = false;
        btn.textContent = originalText;
        alert('Add to cart failed: ' + err.message);
      }
    }
  });
}
